import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, Observable, of, tap } from 'rxjs';

import { PaginationInterface } from 'app/interfaces/pagination.model';
import { AbstractDataStore } from 'v2/data-store/abstract.data-store';
import { StateData } from 'v2/data-store/data-store.types';
import { State } from 'v2/data-store/state';
import { ITableFilter } from 'v2/modules/shared/interfaces/table.interface';
import { createHttpParamsFromTableFilter } from 'v2/shared/api/api.utils';
import { IPaginatedResponse } from 'v2/shared/api/paginated-response.interface';

import {
  CostLayerChangesParamsBySubset,
  ECostLayerChangesSubset,
  isECostLayerChangesSubset,
  ECostLayerRelation,
} from '../cost-layers.const';
import { ICostLayer, ICostLayerChange } from '../cost-layers.types';

import { CostLayersApiService } from './cost-layers-api.service';

//

export interface ICostLayersState extends StateData {
  costLayers :ICostLayer[];
  costLayersLoading :boolean;
  costLayersPaging :PaginationInterface;
  viewingChanges :ICostLayerChange[];
  viewingChangesLoading :boolean;
  viewingChangesPaging :PaginationInterface;
  viewingChangesSubset :ECostLayerChangesSubset;
  viewingChangesTo :ICostLayer['id'] | null;
  viewingCostLayer :ICostLayer;
  viewingCostLayerLoading :boolean;
}

interface _ICostLayersDataStoreBasePagedOptions {
  filters ?:ITableFilter;
  /**
   * Shortcut to load a specific page instead of putting it in filters.
   * Supersedes other ways of specifying the paging params.
   */
  page ?:number;
  pageSize ?:number;
}

export interface ICostLayersDataStoreLoadOptions
  extends _ICostLayersDataStoreBasePagedOptions {
  relations ?:ECostLayerRelation[];
}

export interface ICostLayersDataStoreLoadLayerOptions {
  /** @default false */
  forceFetch ?:boolean;
  relations ?:ECostLayerRelation[];
}

export interface ICostLayersDataStoreLoadLayerChangesOptions
  extends _ICostLayersDataStoreBasePagedOptions {
  /** @default this.state~viewingChangesSubset */
  subset ?:ECostLayerChangesSubset;
  relations ?:string[];
}

const _InitialState = {
  COST_LAYERS: {
    costLayers: [],
    costLayersLoading: false,
    costLayersPaging: null,
  },
  VIEWING_CHANGES: {
    viewingChanges: [],
    viewingChangesLoading: false,
    viewingChangesPaging: null,
    viewingChangesSubset: ECostLayerChangesSubset.ALL,
    viewingChangesTo: null,
  },
  VIEWING_COST_LAYER: {
    viewingCostLayer: null,
    viewingCostLayerLoading: false,
  },
};

//

@Injectable({
  providedIn: 'root',
})
export class CostLayersDataStore extends AbstractDataStore<
  ICostLayersState,
  State<ICostLayersState>
> {
  readonly state = new State<ICostLayersState>({
    ..._InitialState.COST_LAYERS,
    ..._InitialState.VIEWING_CHANGES,
    ..._InitialState.VIEWING_COST_LAYER,
  });
  readonly state$ :Observable<ICostLayersState> = this.state.$;

  readonly costLayers$ :Observable<ICostLayer[]> = this.state.observe('costLayers');
  readonly costLayersLoading$ :Observable<boolean> =
    this.state.observe('costLayersLoading');
  readonly costLayersPaging$ :Observable<PaginationInterface> =
    this.state.observe('costLayersPaging');
  readonly viewingChanges$ :Observable<ICostLayerChange[]> =
    this.state.observe('viewingChanges');
  readonly viewingChangesLoading$ :Observable<boolean> = this.state.observe(
    'viewingChangesLoading',
  );
  readonly viewingChangesPaging$ :Observable<PaginationInterface> =
    this.state.observe('viewingChangesPaging');
  readonly viewingChangesSubset$ :Observable<ECostLayerChangesSubset> =
    this.state.observe('viewingChangesSubset');
  readonly viewingChangesTo$ :Observable<ICostLayer['id']> =
    this.state.observe('viewingChangesTo');
  readonly viewingCostLayer$ :Observable<ICostLayer> =
    this.state.observe('viewingCostLayer');
  readonly viewingCostLayerLoading$ :Observable<boolean> = this.state.observe(
    'viewingCostLayerLoading',
  );

  constructor(private api :CostLayersApiService) {
    super();
    this.init();
  }

  clearCostLayers() {
    this.updateState({ ..._InitialState.COST_LAYERS });
  }
  clearViewingChanges() {
    this.updateState({ ..._InitialState.VIEWING_CHANGES });
  }
  clearViewingCostLayer() {
    this.updateState({ ..._InitialState.VIEWING_COST_LAYER });
  }

  load(
    options ?:ICostLayersDataStoreLoadOptions,
  ) :Observable<IPaginatedResponse<ICostLayer>> {
    // fill options with defaults
    options = {
      filters: null,
      page: null,
      pageSize: null,
      // relations: default is to leave relations _unset_ (not null)
      ...options,
    };

    // process the options
    if (options.page) {
      options.filters = {
        ...options.filters,
        pagination: {
          ...options.filters?.pagination,
          page: options.page,
          pageSize: options.pageSize ?? this.get('costLayersPaging').per_page,
        },
      };
    }
    if (options.relations) {
      options.filters = {
        ...options.filters,
        relations: options.relations,
      };
    }

    // build the proper http params object
    const params :HttpParams = options?.filters
      ? createHttpParamsFromTableFilter(options.filters)
      : null;

    // do it
    this.updateState({ costLayersLoading: true });

    return this.api.get(params).pipe(
      finalize(() => this.updateState({ costLayersLoading: false })),
      tap((response :IPaginatedResponse<ICostLayer>) => {
        const { data, meta } = response;

        this.updateState({
          costLayers: data,
          costLayersLoading: false,
          costLayersPaging: meta,
        });
      }),
    );
  }

  loadLayer(
    id :ICostLayer['id'],
    options ?:ICostLayersDataStoreLoadLayerOptions,
  ) :Observable<ICostLayer> {
    // fill options with defaults
    options = {
      forceFetch: false,
      relations: null,
      ...options,
    };

    // try to load from the existing loaded array
    if (!options.forceFetch) {
      const viewingCostLayer = this.get('costLayers').find(
        (layer :ICostLayer) => layer.id === id,
      );

      if (viewingCostLayer) {
        this.updateState({ viewingCostLayer: viewingCostLayer });

        return of(viewingCostLayer);
      }
    }

    // process the options
    const params :HttpParamsHash = {};

    if (options.relations) {
      params.relations = options.relations.join('|');
    }

    // do it
    this.updateState({ viewingCostLayerLoading: true });

    return this.api.getById(id, params).pipe(
      finalize(() => this.updateState({ viewingCostLayerLoading: false })),
      tap((response :ICostLayer) => {
        this.updateState({
          viewingCostLayer: response,
          viewingCostLayerLoading: false,
        });
      }),
    );
  }

  loadLayerChanges(
    id :ICostLayer['id'],
    options ?:ICostLayersDataStoreLoadLayerChangesOptions,
  ) :Observable<IPaginatedResponse<ICostLayerChange>> {
    const { viewingChangesPaging, viewingChangesSubset } = this.getState();

    // fill options with defaults
    options = {
      filters: null,
      page: null,
      pageSize: null,
      // relations: UNSET,
      subset: viewingChangesSubset,
      ...options,
    };

    // prep the options
    if (!isECostLayerChangesSubset(options.subset)) {
      options.subset = ECostLayerChangesSubset.ALL;
    }
    if (options.page || options.pageSize) {
      options.filters = {
        ...options.filters,
        pagination: {
          ...options.filters?.pagination,
          page: options.page ?? viewingChangesPaging.current_page ?? 1,
          pageSize: options.pageSize ?? viewingChangesPaging.per_page,
        },
      };
    }
    if (options.relations) {
      options.filters = {
        ...options.filters,
        relations: options.relations,
      };
    }

    // do it
    const params = this.#buildLoadLayerChangesParams(options);

    this.updateState({ viewingChangesLoading: true });

    return this.api.getChanges(id, params).pipe(
      finalize(() => this.updateState({ viewingChangesLoading: false })),
      tap((response :IPaginatedResponse<ICostLayerChange>) => {
        const { data, meta } = response;

        this.updateState({
          viewingChanges: data,
          viewingChangesLoading: false,
          viewingChangesPaging: meta,
          viewingChangesSubset: options.subset,
          viewingChangesTo: id,
        });
      }),
    );
  }

  #buildLoadLayerChangesParams(
    options :ICostLayersDataStoreLoadLayerChangesOptions,
  ) :HttpParams | null {
    let params :HttpParams = null;

    if (options.filters) {
      params = createHttpParamsFromTableFilter(options.filters);
    }

    if ([ECostLayerChangesSubset.QUANTITY].includes(options.subset)) {
      params = params
        ? params.appendAll(CostLayerChangesParamsBySubset[options.subset])
        : new HttpParams({
            fromObject: CostLayerChangesParamsBySubset[options.subset],
          });
    }

    return params;
  }
}
